import { HyperbeatStakingCoreAbi } from '../abis/hyperbeat'
import { TotalSupplyAbi } from '../abis/shared'
import type { LstRegistryEntry } from '../registry'
import { ONE_E18, toBigInt, type LstReader } from './shared'

/**
 * Hyperbeat beHYPE reader.
 *
 * Two calls: `beHYPE.totalSupply()` + `StakingCore.BeHYPEToHYPE(1e18)`
 * (1e18-scaled HYPE-per-beHYPE).
 *
 * Despite the "Hyperbeat × ether.fi" branding, this is **not** an
 * `AccountantWithRateProviders` — the rate getter follows the same
 * `XToY` pattern as Kinetiq. `entry.readerExtras.hyperbeatStakingCore`
 * pins the address.
 *
 * Fall back to `offChain`-style placeholder if absent.
 */
export const readerHyperbeatBeHype = (entry: LstRegistryEntry): LstReader => {
  const stakingCore = entry.readerExtras?.hyperbeatStakingCore
  if (!stakingCore) {
    return {
      calls: [{ address: entry.address, name: 'totalSupply', params: [] }],
      abis: [TotalSupplyAbi],
      parse: ([supply]) => {
        const totalSupply = toBigInt(supply)
        if (totalSupply === undefined) return undefined
        return {
          totalAssets: totalSupply,
          totalSupply,
          exchangeRate: ONE_E18,
        }
      },
    }
  }
  return {
    calls: [
      { address: entry.address, name: 'totalSupply', params: [] },
      { address: stakingCore, name: 'BeHYPEToHYPE', params: [ONE_E18] },
    ],
    abis: [TotalSupplyAbi, HyperbeatStakingCoreAbi],
    parse: ([supply, rate]) => {
      const totalSupply = toBigInt(supply)
      const exchangeRate = toBigInt(rate)
      if (totalSupply === undefined || exchangeRate === undefined) {
        return undefined
      }
      return {
        totalAssets: (totalSupply * exchangeRate) / ONE_E18,
        totalSupply,
        exchangeRate,
      }
    },
  }
}
